export interface PocCandidate {
  id: string;
  name: string;
  party: string;
  position: string;
  image: string;
  votes: number;
  percentage: number;
  state?: string;
  bio: string;
  education: string[];
  experience: string[];
  platform: string[];
  socialMedia: { twitter?: string; instagram?: string; facebook?: string };
  followers?: number;
  supporters?: number;
  engagementRate?: number;
}

export interface ElectionResult {
  id: string;
  position: string;
  state: string;
  totalVotes: number;
  totalRegistered: number;
  turnout: number;
  candidates: PocCandidate[];
  timestamp: Date;
}

export interface SocialMediaTrend {
  id: string;
  candidate: string;
  mentions: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  percentage: number;
  platform: 'Twitter' | 'Instagram' | 'Facebook' | 'General News';
  date: Date;
}

export interface ElectionMetrics {
  totalRegisteredVoters: number;
  totalVotesPolled: number;
  turnoutPercentage: number;
  statesCovered: number;
  pollingSUnitsCovered: number;
  timestamp: Date;
}

export interface StateData {
  id: string;
  name: string;
  region: string;
  turnout: number;
  leadingCandidate?: string;
  percentage?: number;
}

export const mockPocCandidates: PocCandidate[] = [
  {
    id: '1', name: 'Chioma Adeyemi', party: 'Progressive Alliance', position: 'Presidential Candidate',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop',
    votes: 8450000, percentage: 35.2,
    bio: 'Experienced economist and public administrator with 20+ years in governance',
    education: ['Ph.D. Economics - University of Lagos', 'MBA - London Business School'],
    experience: ['Former Governor - Lagos State', 'Minister of Economic Affairs', 'Chief Executive - National Planning Commission'],
    platform: ['Economic Recovery', 'Job Creation', 'Education Reform', 'Healthcare Modernization'],
    socialMedia: { twitter: '@ChiomaAdeyemi', instagram: '@chioma_adeyemi', facebook: 'ChiomaAdeyemiOfficial' },
    followers: 2800000, supporters: 3200000, engagementRate: 8.5, state: 'National'
  },
  {
    id: '2', name: 'Adebayo Okonkwo', party: 'Unity Front', position: 'Presidential Candidate',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    votes: 6720000, percentage: 28.0,
    bio: 'Military officer turned administrator, advocate for national unity and security',
    education: ['B.Sc. Political Science - University of Ibadan', 'National Defence Academy'],
    experience: ['Former Army Chief of Staff', 'National Security Advisor', 'Governor - Kano State'],
    platform: ['National Security', 'Infrastructure Development', 'Anti-Corruption', 'Agricultural Reform'],
    socialMedia: { twitter: '@AdebayoOkonkwo', instagram: '@adebayo_okonkwo_official', facebook: 'AdebayoOkonkwoForNigeria' },
    followers: 2100000, supporters: 2450000, engagementRate: 7.2, state: 'National'
  },
  {
    id: '3', name: 'Zainab Mohammed', party: 'Democratic Reform Party', position: 'Presidential Candidate',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop',
    votes: 5340000, percentage: 22.2,
    bio: 'Human rights activist and businesswoman committed to democratic strengthening',
    education: ['B.A. Law - University of Nigeria', 'Masters - Oxford University'],
    experience: ['Founder - Civil Rights Organization', 'Member - National Assembly', 'Business Executive'],
    platform: ['Democracy Strengthening', 'Women Empowerment', 'Environmental Protection', 'Judicial Reform'],
    socialMedia: { twitter: '@ZainabMohammed', instagram: '@zainab.mohammed', facebook: 'ZainabMohammedVision' },
    followers: 1950000, supporters: 2150000, engagementRate: 9.1, state: 'National'
  },
  {
    id: '4', name: 'Kunle Adebiyi', party: 'Progress Movement', position: 'Presidential Candidate',
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop',
    votes: 3490000, percentage: 14.6,
    bio: 'Technology entrepreneur focused on digital transformation and innovation',
    education: ['B.Eng. Computer Science - UNILAG', 'Startup Leadership - Stanford University'],
    experience: ['Founder - Multiple Tech Companies', 'Digital Minister Advisor', 'Tech Innovation Board'],
    platform: ['Digital Innovation', 'Tech Startups Support', 'Energy Transition', 'Youth Development'],
    socialMedia: { twitter: '@KunleAdebiyi', instagram: '@kunle.adebiyi', facebook: 'KunleAdebiyiForChange' },
    followers: 1200000, supporters: 980000, engagementRate: 6.8, state: 'National'
  }
];

export const mockElectionResults: ElectionResult[] = [
  { id: '1', position: 'Presidential', state: 'Lagos', totalVotes: 4250000, totalRegistered: 6800000, turnout: 62.5, candidates: mockPocCandidates, timestamp: new Date('2024-02-25') },
  { id: '2', position: 'Presidential', state: 'Abuja', totalVotes: 1850000, totalRegistered: 2900000, turnout: 63.8, candidates: mockPocCandidates, timestamp: new Date('2024-02-25') },
  { id: '3', position: 'Presidential', state: 'Kano', totalVotes: 3200000, totalRegistered: 5100000, turnout: 62.7, candidates: mockPocCandidates, timestamp: new Date('2024-02-25') },
];

export const mockSocialMediaTrends: SocialMediaTrend[] = [
  { id: '1', candidate: 'Chioma Adeyemi', mentions: 125430, sentiment: 'positive', percentage: 68, platform: 'Twitter', date: new Date() },
  { id: '2', candidate: 'Adebayo Okonkwo', mentions: 98540, sentiment: 'positive', percentage: 62, platform: 'Twitter', date: new Date() },
  { id: '3', candidate: 'Zainab Mohammed', mentions: 87320, sentiment: 'positive', percentage: 71, platform: 'Twitter', date: new Date() },
  { id: '4', candidate: 'Kunle Adebiyi', mentions: 45680, sentiment: 'neutral', percentage: 58, platform: 'Twitter', date: new Date() },
  { id: '5', candidate: 'Chioma Adeyemi', mentions: 89230, sentiment: 'positive', percentage: 72, platform: 'Instagram', date: new Date() },
  { id: '6', candidate: 'Zainab Mohammed', mentions: 76540, sentiment: 'positive', percentage: 65, platform: 'Instagram', date: new Date() },
  { id: '7', candidate: 'Adebayo Okonkwo', mentions: 156780, sentiment: 'positive', percentage: 75, platform: 'Facebook', date: new Date() },
  { id: '8', candidate: 'Chioma Adeyemi', mentions: 134520, sentiment: 'positive', percentage: 69, platform: 'Facebook', date: new Date() },
  { id: '9', candidate: 'Zainab Mohammed', mentions: 98340, sentiment: 'neutral', percentage: 61, platform: 'Facebook', date: new Date() },
  { id: '10', candidate: 'Kunle Adebiyi', mentions: 67890, sentiment: 'positive', percentage: 63, platform: 'Facebook', date: new Date() },
  { id: '11', candidate: 'Chioma Adeyemi', mentions: 234560, sentiment: 'positive', percentage: 82, platform: 'General News', date: new Date() },
  { id: '12', candidate: 'Adebayo Okonkwo', mentions: 198760, sentiment: 'neutral', percentage: 71, platform: 'General News', date: new Date() },
  { id: '13', candidate: 'Zainab Mohammed', mentions: 187430, sentiment: 'positive', percentage: 78, platform: 'General News', date: new Date() },
  { id: '14', candidate: 'Kunle Adebiyi', mentions: 123450, sentiment: 'neutral', percentage: 65, platform: 'General News', date: new Date() },
];

export const mockElectionMetrics: ElectionMetrics = {
  totalRegisteredVoters: 87400000, totalVotesPolled: 54200000,
  turnoutPercentage: 62.0, statesCovered: 36, pollingSUnitsCovered: 176846, timestamp: new Date()
};

export const mockStates: StateData[] = [
  // Northwest
  { id: 'kano', name: 'Kano', region: 'Northwest', turnout: 58.9 },
  { id: 'katsina', name: 'Katsina', region: 'Northwest', turnout: 52.3 },
  { id: 'kaduna', name: 'Kaduna', region: 'Northwest', turnout: 57.8 },
  { id: 'sokoto', name: 'Sokoto', region: 'Northwest', turnout: 48.6 },
  { id: 'zamfara', name: 'Zamfara', region: 'Northwest', turnout: 44.2 },
  { id: 'kebbi', name: 'Kebbi', region: 'Northwest', turnout: 46.9 },
  { id: 'jigawa', name: 'Jigawa', region: 'Northwest', turnout: 51.7 },
  // Northeast
  { id: 'borno', name: 'Borno', region: 'Northeast', turnout: 42.1 },
  { id: 'yobe', name: 'Yobe', region: 'Northeast', turnout: 45.8 },
  { id: 'adamawa', name: 'Adamawa', region: 'Northeast', turnout: 55.3 },
  { id: 'bauchi', name: 'Bauchi', region: 'Northeast', turnout: 53.6 },
  { id: 'gombe', name: 'Gombe', region: 'Northeast', turnout: 56.2 },
  { id: 'taraba', name: 'Taraba', region: 'Northeast', turnout: 49.7 },
  // North-Central
  { id: 'niger', name: 'Niger', region: 'North-Central', turnout: 50.4 },
  { id: 'kwara', name: 'Kwara', region: 'North-Central', turnout: 60.8 },
  { id: 'kogi', name: 'Kogi', region: 'North-Central', turnout: 58.1 },
  { id: 'benue', name: 'Benue', region: 'North-Central', turnout: 61.5 },
  { id: 'plateau', name: 'Plateau', region: 'North-Central', turnout: 63.7 },
  { id: 'nassarawa', name: 'Nassarawa', region: 'North-Central', turnout: 55.9 },
  { id: 'fct', name: 'Federal Capital Territory', region: 'North-Central', turnout: 65.2 },
  // Southwest
  { id: 'lagos', name: 'Lagos', region: 'Southwest', turnout: 68.5 },
  { id: 'ogun', name: 'Ogun', region: 'Southwest', turnout: 62.4 },
  { id: 'oyo', name: 'Oyo', region: 'Southwest', turnout: 66.1 },
  { id: 'osun', name: 'Osun', region: 'Southwest', turnout: 64.3 },
  { id: 'ondo', name: 'Ondo', region: 'Southwest', turnout: 59.7 },
  { id: 'ekiti', name: 'Ekiti', region: 'Southwest', turnout: 67.2 },
  // South-South
  { id: 'edo', name: 'Edo', region: 'South-South', turnout: 62.8 },
  { id: 'delta', name: 'Delta', region: 'South-South', turnout: 57.6 },
  { id: 'rivers', name: 'Rivers', region: 'South-South', turnout: 61.3 },
  { id: 'bayelsa', name: 'Bayelsa', region: 'South-South', turnout: 54.8 },
  { id: 'cross-river', name: 'Cross River', region: 'South-South', turnout: 58.4 },
  { id: 'akwa-ibom', name: 'Akwa Ibom', region: 'South-South', turnout: 60.1 },
  // Southeast
  { id: 'anambra', name: 'Anambra', region: 'Southeast', turnout: 71.2 },
  { id: 'enugu', name: 'Enugu', region: 'Southeast', turnout: 59.4 },
  { id: 'ebonyi', name: 'Ebonyi', region: 'Southeast', turnout: 56.8 },
  { id: 'imo', name: 'Imo', region: 'Southeast', turnout: 63.5 },
  { id: 'abia', name: 'Abia', region: 'Southeast', turnout: 61.9 },
];

export const CHART_COLORS = ['#084608', '#79BD79', '#C2422B', '#7E9F7E'];

export const PARTY_COLORS: Record<string, string> = {
  'Progressive Alliance': '#1976d2',
  'Unity Front': '#d32f2f',
  'Democratic Reform Party': '#7b1fa2',
  'Progress Movement': '#388e3c',
};

export const SENTIMENT_COLORS: Record<string, string> = {
  positive: '#79BD79',
  neutral: '#7E9F7E',
  negative: '#C2422B',
};
